import ServiceBase from "@app/shared/serviceBase";
import { ClientListItem } from "@app/client/client";
import {
  AdequacyReviewData,
  CreateNetworkSubmissionRequest,
  ExceptionReviewData,
  GapDetailsQuery,
  NetworkProfileChartSummary,
  NetworkProfileGapDetailsResponse,
  NetworkProfileMetrics,
  NetworkProfileOverview,
  NetworkSubmissionData,
  NetworkSubmissionMetricsQuery,
  NetworkSubmissionProjectsQuery,
  NetworkSubmissionProjectResponse,
  NetworkSubmissionsQuery,
  NetworkSubmissionMetrics,
  NetworkSubmissionListItem,
  SubmissionCommentDisplay,
  SubmissionCommentListApiResponse
} from "@app/compliance-connect/network-submission/interfaces";

export default class NetworkSubmissionService extends ServiceBase {
  public async getProjects(query: NetworkSubmissionProjectsQuery, signal?: AbortSignal): Promise<NetworkSubmissionProjectResponse> {
    return await this.makeGet<NetworkSubmissionProjectResponse>(
      "networksubmission/projects",
      query,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getProjectTemplates(signal?: AbortSignal): Promise<string[]> {
    return await this.makeGet<string[]>(
      "networksubmission/projects/templates",
      undefined,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getAdequacyReview(projectId: number, countyIds?: string[], specialtyIds?: string[], signal?: AbortSignal): Promise<AdequacyReviewData> {
    return await this.makeGet<AdequacyReviewData>(
      `networksubmission/projects/${projectId}/adequacy`,
      { countyIds, specialtyIds },
      undefined,
      false,
      undefined,
      signal);
  }

  public async getExceptionReview(projectId: number, countyIds?: string[], specialtyIds?: string[], signal?: AbortSignal): Promise<ExceptionReviewData> {
    return await this.makeGet<ExceptionReviewData>(
      `networksubmission/projects/${projectId}/exceptions`,
      { countyIds, specialtyIds },
      undefined,
      false,
      undefined,
      signal);
  }

  public async getNetworkProfileMetrics(submissionId: number, signal?: AbortSignal): Promise<NetworkProfileMetrics> {
    return await this.makeGet<NetworkProfileMetrics>(
      `networksubmission/submissions/${submissionId}/metrics`,
      undefined,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getNetworkProfileChartSummary(submissionId: number, signal?: AbortSignal): Promise<NetworkProfileChartSummary> {
    return await this.makeGet<NetworkProfileChartSummary>(
      `networksubmission/submissions/${submissionId}/chart-summary`,
      undefined,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getNetworkProfileOverview(submissionId: number, signal?: AbortSignal): Promise<NetworkProfileOverview> {
    return await this.makeGet<NetworkProfileOverview>(
      `networksubmission/submissions/${submissionId}/overview`,
      undefined,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getSubmissionComments(submissionId: number, signal?: AbortSignal): Promise<SubmissionCommentDisplay[]> {
    const response = await this.makeGet<SubmissionCommentListApiResponse>(
      `networksubmission/submissions/${submissionId}/comments`,
      undefined,
      undefined,
      false,
      undefined,
      signal);

    return response.comments;
  }

  public async markSubmissionCommentAsRead(submissionId: number, commentId: number): Promise<SubmissionCommentDisplay> {
    return this.makePatch<SubmissionCommentDisplay>(
      `networksubmission/submissions/${submissionId}/comments/${commentId}/review`);
  }

  public async checkNameExists(name: string, signal?: AbortSignal): Promise<boolean> {
    return this.makeGet<boolean>(
      "networksubmission/submissions/name-exists",
      { name },
      undefined,
      false,
      undefined,
      signal);
  }

  public async getRegulators(signal?: AbortSignal): Promise<ClientListItem[]> {
    return await this.makeGet<ClientListItem[]>(
      "networksubmission/regulators",
      undefined,
      undefined,
      false,
      undefined,
      signal);
  }

  public async createSubmission(payload: CreateNetworkSubmissionRequest): Promise<{ submissionId: number }> {
    return await this.makePost<{ submissionId: number }>(
      "networksubmission/submissions",
      {
        comments: payload.comments,
        emailSharedUsers: payload.emailSharedUsers,
        exceptions: payload.pendingExceptions,
        projectId: payload.projectId,
        regulatorClientId: payload.regulatorClientId,
        sharedUserIds: payload.sharedUserIds,
        submissionDescription: payload.submissionDescription,
        submissionName: payload.submissionName,
      });
  }

  public async getSubmissionMetrics(query: NetworkSubmissionMetricsQuery, signal?: AbortSignal): Promise<NetworkSubmissionMetrics> {
    return await this.makeGet<NetworkSubmissionMetrics>(
      "networksubmission/metrics",
      query,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getGapDetails(submissionId: number, query: GapDetailsQuery, signal?: AbortSignal): Promise<NetworkProfileGapDetailsResponse> {
    return await this.makeGet<NetworkProfileGapDetailsResponse>(
      `networksubmission/submissions/${submissionId}/gap-details`,
      query,
      undefined,
      false,
      undefined,
      signal);
  }

  public async getSubmissions(query: NetworkSubmissionsQuery, signal?: AbortSignal): Promise<{ networkSubmissions: NetworkSubmissionData[]; totalCount: number }> {
    const response = await this.makeGet<{ networkSubmissions: NetworkSubmissionListItem[]; totalCount: number }>(
      "networksubmission/submissions",
      query,
      undefined,
      false,
      undefined,
      signal);

    return {
      networkSubmissions: response.networkSubmissions.map(item => ({
        counties: item.counties,
        customProjectId: item.customProjectId,
        dateSubmitted: item.dateSubmitted,
        exceptions: item.exceptions,
        fillableGaps: item.fillableGaps,
        gaps: item.gaps,
        key: item.submissionId.toString(),
        membershipCount: item.membershipCount,
        projectName: item.projectName,
        projectRunConfigurationId: item.projectRunConfigurationId,
        regionSetCode: "",
        regulatorComments: item.regulatorComments,
        regulatorName: item.regulatorName,
        regulatorReviewStatus: item.regulatorReviewStatus,
        specialties: item.specialties,
        specialtyGroupCode: "",
      })),
      totalCount: response.totalCount,
    };
  }
}
